import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { fetchEvent } from '../api/public'
import { TicketsShell } from '../layout/TicketsShell'
import { usePageMeta } from '../hooks/usePageMeta'
import { cmsImageSocialUrl } from '../lib/cms-image'
import { schemaGraph, schemaTicketPage } from '../lib/schema-org'

const BOOKINEA_SHOP_ORIGIN = 'https://bunkerevents.shop.bookinea.app'

function resolveShopUrl(ticketUrl: string | null | undefined): string | null {
  if (!ticketUrl?.trim()) return null
  const raw = ticketUrl.trim()
  if (/^https?:\/\//i.test(raw)) return raw
  const path = raw.startsWith('/') ? raw : `/${raw}`
  return `${BOOKINEA_SHOP_ORIGIN}${path}`
}

export function EventTicketsPage() {
  const { slug } = useParams<{ slug: string }>()

  const eventQ = useQuery({
    queryKey: ['event', slug],
    queryFn: () => fetchEvent(slug!),
    enabled: !!slug,
    retry: false,
  })

  const ev = eventQ.data
  const shopUrl = resolveShopUrl(ev?.ticketUrl)

  const jsonLd = useMemo(
    () => (ev && shopUrl ? schemaGraph(...schemaTicketPage(ev, shopUrl)) : null),
    [ev, shopUrl],
  )

  usePageMeta(
    ev
      ? {
          title: `Tickets — ${ev.title}`,
          description:
            ev.summary?.trim() ||
            `Tickets und Checkout für ${ev.title}. Sichere dir deinen Platz bei BNKR.`,
          canonical: `/events/${ev.slug}/tickets`,
          ogImage: cmsImageSocialUrl(ev.featuredImageUrl) ?? ev.featuredImageUrl,
          ogType: 'website',
          jsonLd,
        }
      : slug
        ? { title: 'Tickets', description: 'Event-Tickets bei BNKR.', canonical: `/events/${slug}/tickets` }
        : null,
  )

  if (!slug) return null

  if (eventQ.isLoading) {
    return (
      <TicketsShell backTo="/events">
        <p className="tickets-shell__status">Laden…</p>
      </TicketsShell>
    )
  }

  if (eventQ.isError || !ev) {
    return (
      <TicketsShell backTo="/events">
        <p className="tickets-shell__status">Event nicht gefunden.</p>
      </TicketsShell>
    )
  }

  if (!shopUrl) {
    return (
      <TicketsShell backTo={`/events/${slug}`} backLabel="← Zum Event">
        <p className="tickets-shell__status">Tickets sind für dieses Event noch nicht verfügbar.</p>
      </TicketsShell>
    )
  }

  return (
    <TicketsShell backTo={`/events/${slug}`} backLabel="← Zum Event">
      <iframe
        className="tickets-shell__iframe"
        src={shopUrl}
        title={`Tickets — ${ev.title}`}
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </TicketsShell>
  )
}
